import React, { useState, useRef, useEffect, useMemo } from 'react';
import { 
  Equipment, 
  InspectionPlan, 
  InspectionActual, 
  InspectionStatus, 
  Finding,
  FollowUpStatus,
  InspectionType,
  FindingLevel,
  SQCDME,
  BusinessRisk,
  AppUser,
  PlanStatus
} from '../types';
import { AreaConfig } from '../App';
import { 
  PenLine, 
  CheckCircle2, 
  ChevronRight,
  Camera,
  X,
  Zap,
  Layout,
  ClipboardCheck,
  ShieldAlert,
  MapPin,
  Info,
  Users,
  Activity,
  AlertTriangle,
  Search,
  Target,
  Check,
  ShieldCheck,
  QrCode,
  Maximize,
  ArrowRight,
  History
} from 'lucide-react';
import { INSPECTION_TYPE_ICONS } from '../constants';
import { Html5QrcodeScanner } from 'html5-qrcode';

interface InputActualProps {
  equipments: Equipment[];
  areaConfigs: AreaConfig[];
  plans: InspectionPlan[];
  actuals: InspectionActual[];
  addActual: (actual: InspectionActual) => void;
  addFinding: (finding: Finding) => void;
  updatePlan?: (plan: InspectionPlan) => void;
  user: AppUser;
  selectedId?: string | null;
  clearSelection?: () => void;
}

const compressImage = (base64Str: string, maxWidth = 1024, quality = 0.7): Promise<string> => {
  return new Promise((resolve) => {
    const img = new Image();
    img.src = base64Str;
    img.onload = () => {
      const canvas = document.createElement('canvas');
      let width = img.width;
      let height = img.height;

      if (width > maxWidth) {
        height = (height * maxWidth) / width;
        width = maxWidth;
      }

      canvas.width = width;
      canvas.height = height;
      const ctx = canvas.getContext('2d');
      ctx?.drawImage(img, 0, 0, width, height);
      resolve(canvas.toDataURL('image/jpeg', quality));
    };
  });
};

const InputActual: React.FC<InputActualProps> = ({ 
  equipments, 
  areaConfigs, 
  plans, 
  actuals,
  addActual, 
  addFinding, 
  updatePlan,
  user,
  selectedId,
  clearSelection
}) => {
  const [method, setMethod] = useState<'routine' | 'genba' | null>(null);
  const [success, setSuccess] = useState(false);
  const [showScanner, setShowScanner] = useState(false);
  const [isCompressing, setIsCompressing] = useState(false);
  const photoInputRef = useRef<HTMLInputElement>(null);
  const scannerRef = useRef<Html5QrcodeScanner | null>(null);

  const todayStr = new Date().toISOString().split('T')[0];
  const mustCheckToday = useMemo(() => {
    return plans.filter(p => {
        const isToday = p.plannedDate === todayStr;
        const isPast = p.plannedDate < todayStr;
        
        // As per requirement: Task only removed if status is COMPLETED officially
        const isCompleted = p.status === PlanStatus.COMPLETED;

        return (isToday || isPast) && !isCompleted;
    })
    .sort((a, b) => a.plannedDate.localeCompare(b.plannedDate));
    // Removed slice to ensure full visibility as requested by user
  }, [plans, todayStr]);

  const stats = useMemo(() => {
    const todayPlans = plans.filter(p => p.plannedDate === todayStr);
    const overduePlans = plans.filter(p => p.plannedDate < todayStr && p.status !== PlanStatus.COMPLETED);
    
    const completedToday = todayPlans.filter(p => p.status === PlanStatus.COMPLETED).length;
    const totalTarget = todayPlans.length + overduePlans.length;
    
    return {
      totalToday: totalTarget,
      completedToday: completedToday,
      overdue: overduePlans.length,
      rate: totalTarget > 0 ? Math.round((completedToday / totalTarget) * 100) : 100
    };
  }, [plans, todayStr]);

  const [form, setForm] = useState({
    inspectionType: InspectionType.ROUTINE,
    equipmentId: '', 
    planId: '',
    area: areaConfigs[0]?.name || '',
    section: '', 
    actualDate: new Date().toISOString().split('T')[0],
    status: InspectionStatus.OK,
    inspector: user.name,
    notes: '',
    evidencePhoto: '',
    findingLevel: FindingLevel.MINOR,
    focusSQCDME: SQCDME.SAFETY,
    businessRisk: BusinessRisk.COMPLIANCE,
    managementPresent: '',
    bodRecommendation: '',
    pic: '' 
  });

  // Handle selectedId from external navigation (e.g. Reports)
  useEffect(() => {
    if (selectedId) {
      const eq = equipments.find(e => e.id === selectedId);
      if (eq) {
        const relevantPlan = plans.find(p => 
          p.equipmentId === eq.id && 
          !actuals.some(a => a.planId === p.id || (a.equipmentId === p.equipmentId && a.actualDate === p.plannedDate))
        );

        setMethod('routine');
        setForm(prev => ({
          ...prev,
          inspectionType: InspectionType.ROUTINE,
          equipmentId: eq.id,
          planId: relevantPlan?.id || '',
          area: eq.area,
          section: eq.section || ''
        }));

        if (relevantPlan && updatePlan && relevantPlan.status !== PlanStatus.COMPLETED) {
            updatePlan({ ...relevantPlan, status: PlanStatus.IN_PROGRESS });
        }
      }
      // clear selection after processing to avoid re-triggering if method changes
      clearSelection?.();
    }
  }, [selectedId, equipments, plans, actuals, clearSelection]);

  const [checkItems, setCheckItems] = useState<Record<string, boolean>>({});
  const isRoutine = form.inspectionType === InspectionType.ROUTINE;
  const isObservationMode = form.inspectionType !== InspectionType.ROUTINE;

  const observationTypes = [
    InspectionType.GENBA_WALK, InspectionType.GENBA_BOD, InspectionType.SAFETY_PATROL,
    InspectionType.FIRE_ELECTRICAL, InspectionType.WORKPLACE_HEALTH, InspectionType.LIFTING_EQUIPMENT,
    InspectionType.PROD_RISK, InspectionType.CHEMICAL_PAINTING, InspectionType.SAFETY_CULTURE,
    InspectionType.FIVE_S, InspectionType.ENV_WALK, InspectionType.FIRE_SAFETY,
    InspectionType.ENERGY_WALK, InspectionType.MANAGEMENT_WALK
  ];

  useEffect(() => {
    if (showScanner) {
      const scanner = new Html5QrcodeScanner("reader", { fps: 10, qrbox: { width: 250, height: 250 } }, false);
      scanner.render(onScanSuccess, onScanFailure);
      scannerRef.current = scanner;
    }
    return () => {
      if (scannerRef.current) {
        scannerRef.current.clear().catch((e: any) => console.error(e));
      }
    };
  }, [showScanner]);

  function onScanSuccess(decodedText: string) {
    const asset = equipments.find(e => e.code === decodedText);
    if (asset) {
      const relevantPlan = plans.find(p => 
        p.equipmentId === asset.id && 
        !actuals.some(a => a.planId === p.id || (a.equipmentId === p.equipmentId && a.actualDate === p.plannedDate))
      );

      setForm(prev => ({
        ...prev,
        equipmentId: asset.id,
        planId: relevantPlan?.id || '',
        area: asset.area,
        section: asset.section || ''
      }));

      if (relevantPlan && updatePlan && relevantPlan.status !== PlanStatus.COMPLETED) {
          updatePlan({ ...relevantPlan, status: PlanStatus.IN_PROGRESS });
      }

      setShowScanner(false);
      alert(`Asset detected: ${asset.code} - ${asset.type}`);
    } else {
      alert(`QR Code ${decodedText} not found in database.`);
    }
  }

  function onScanFailure(error: any) {}

  const filteredEquipments = useMemo(() => {
    return equipments.filter(e => e.area === form.area && e.section === form.section);
  }, [equipments, form.area, form.section]);

  useEffect(() => {
    const area = areaConfigs.find(a => a.name === form.area);
    const sectionData = area?.sections.find(s => s.name === form.section);
    if (sectionData) {
      setForm(prev => ({ ...prev, pic: sectionData.defaultPIC }));
    }
  }, [form.section, form.area, areaConfigs]);

  const selectedAssetInfo = useMemo(() => {
    if (!isRoutine || !form.equipmentId) return null;
    return equipments.find(e => e.id === form.equipmentId);
  }, [form.equipmentId, isRoutine, equipments]);

  useEffect(() => {
    if (isRoutine && selectedAssetInfo) {
      const checklist = selectedAssetInfo.technicalChecklist || [];
      const initialItems: Record<string, boolean> = {};
      checklist.forEach(item => initialItems[item] = true);
      setCheckItems(initialItems);
    } else {
      setCheckItems({});
    }
  }, [selectedAssetInfo, isRoutine]);

  const toggleCheckItem = (item: string) => {
    setCheckItems(prev => ({ ...prev, [item]: !prev[item] }));
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    const newId = `act-${Date.now()}`;
    if (isRoutine && !form.equipmentId) { alert("Pilih aset dahulu!"); return; }

    const hasNG = Object.values(checkItems).some(val => val === false);
    const finalStatus = isRoutine ? (hasNG ? InspectionStatus.NG : InspectionStatus.OK) : form.status;

    const newActual: InspectionActual = { 
        ...form, 
        id: newId, 
        status: finalStatus,
        planId: form.planId || undefined,
        equipmentId: isRoutine ? form.equipmentId : 'OBSERVATION-UNIT', 
        checkItems: isRoutine ? checkItems : undefined,
        managementPresent: form.managementPresent ? form.managementPresent.split(',') : []
    } as any;
    
    addActual(newActual);
    
    if (form.planId && updatePlan) {
        const plan = plans.find(p => p.id === form.planId);
        if (plan) {
            updatePlan({ ...plan, status: PlanStatus.COMPLETED });
        }
    }

    if (finalStatus === InspectionStatus.NG || isObservationMode) {
      addFinding({
        id: `fnd-${Date.now()}`,
        inspectionId: newId,
        date: form.actualDate,
        description: isRoutine ? `NG pada checklist: ${Object.entries(checkItems).filter(([_, v]) => !v).map(([k]) => k).join(', ')}. ${form.notes}` : form.notes,
        area: form.area,
        section: form.section,
        equipmentName: isRoutine ? (selectedAssetInfo ? `${selectedAssetInfo.type} (${selectedAssetInfo.code})` : 'Asset Rutin') : `${form.inspectionType} Finding`,
        pic: form.pic, 
        inspectorName: user.name,
        targetDate: new Date(Date.now() + (form.findingLevel === FindingLevel.CRITICAL ? 7 : 14) * 86400000).toISOString().split('T')[0],
        status: FollowUpStatus.OPEN,
        inspectionType: form.inspectionType,
        level: form.findingLevel,
        focus: form.focusSQCDME,
        businessRisk: form.businessRisk,
        bodRecommendation: form.bodRecommendation
      });
    }

    setSuccess(true);
    setTimeout(() => { setSuccess(false); setMethod(null); }, 1500);
  };

  return (
    <div className="space-y-10 pb-20 animate-in fade-in duration-500">
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-4xl font-black text-slate-900 uppercase tracking-tighter">Reporting Hub</h1>
          <p className="text-slate-500 mt-2 font-medium italic">Record field data with high integrity and detailed observations.</p>
        </div>
        {success && (
          <div className="bg-emerald-500 text-white px-8 py-3 rounded-2xl flex items-center gap-3 animate-bounce font-black text-xs uppercase shadow-2xl">
            <CheckCircle2 className="w-6 h-6" /> Data Synced Successfully
          </div>
        )}
      </div>

      {!method ? (
        <div className="space-y-12">
            {/* MUST CHECK TODAY SECTION */}
            <div className="bg-white p-8 lg:p-12 rounded-[3rem] border border-slate-100 shadow-sm relative overflow-hidden group">
                <div className="absolute top-0 right-0 p-12 opacity-[0.03] pointer-events-none group-hover:scale-110 transition-transform duration-1000">
                    <ShieldAlert className="w-64 h-64 text-rose-500" />
                </div>
                
                <div className="flex flex-col md:flex-row md:items-center justify-between mb-10 gap-6 relative z-10">
                    <div>
                        <h3 className="text-xl font-black text-slate-900 uppercase tracking-tighter flex items-center gap-3">
                            <Target className="w-6 h-6 text-rose-500" /> Priority Task List
                        </h3>
                        <p className="text-[10px] font-black text-slate-400 uppercase tracking-widest mt-2 px-1 flex items-center gap-2">
                           <Activity className="w-3 h-3 text-rose-400" /> Mandatory inspections for field validation
                        </p>
                    </div>
                    <div className="flex gap-2">
                        <div className="px-5 py-3 bg-rose-50 border border-rose-100 rounded-2xl">
                            <p className="text-[8px] font-black text-rose-400 uppercase tracking-widest mb-1">Overdue</p>
                            <p className="text-xl font-black text-rose-600">{stats.overdue}</p>
                        </div>
                        <div className={`px-5 py-3 ${stats.completedToday >= stats.totalToday ? 'bg-emerald-50 border border-emerald-100' : 'bg-amber-50 border border-amber-100'} rounded-2xl`}>
                            <p className="text-[8px] font-black text-emerald-400 uppercase tracking-widest mb-1">Target Progress</p>
                            <p className="text-xl font-black text-emerald-600">{stats.completedToday}/{stats.totalToday}</p>
                        </div>
                        <div className={`px-5 py-3 ${stats.rate >= 100 ? 'bg-indigo-50 border border-indigo-100' : 'bg-rose-50 border border-rose-100'} rounded-2xl`}>
                            <p className="text-[8px] font-black text-indigo-400 uppercase tracking-widest mb-1">Compliance</p>
                            <p className="text-xl font-black text-indigo-600">{stats.rate}%</p>
                        </div>
                    </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 relative z-10">
                    {mustCheckToday.length > 0 ? mustCheckToday.map(p => {
                        const eq = equipments.find(e => e.id === p.equipmentId);
                        const isOverdue = p.plannedDate <= todayStr;
                        return (
                            <div key={p.id} className={`p-6 border rounded-[2rem] flex items-center justify-between group transition-all hover:shadow-xl ${isOverdue ? 'bg-rose-50/30 border-rose-100 hover:border-rose-300' : 'bg-slate-50 border-slate-100 hover:border-emerald-300'}`}>
                                <div className="flex items-center gap-5">
                                    <div className={`w-14 h-14 rounded-2xl flex items-center justify-center shadow-sm text-lg font-black transition-all ${isOverdue ? 'bg-rose-600 text-white shadow-rose-200' : 'bg-white text-slate-400 group-hover:text-emerald-500'}`}>
                                        {eq?.type.charAt(0)}
                                    </div>
                                    <div>
                                        <div className="flex items-center gap-2 mb-1">
                                            <p className="text-sm font-black text-slate-900 tracking-tight">{eq?.code}</p>
                                            <span className={`px-2 py-0.5 rounded-md text-[8px] font-black uppercase tracking-widest animate-pulse ${isOverdue ? 'bg-rose-600 text-white' : 'bg-slate-400 text-white'}`}>
                                                {p.plannedDate === todayStr ? 'DUE TODAY' : 'OVERDUE'}
                                            </span>
                                        </div>
                                        <p className="text-[10px] text-slate-400 font-black uppercase tracking-widest">{p.plannedDate} • {eq?.area}</p>
                                    </div>
                                </div>
                                <button 
                                    onClick={() => {
                                        setMethod('routine');
                                        setForm(prev => ({
                                            ...prev,
                                            inspectionType: InspectionType.ROUTINE,
                                            equipmentId: eq?.id || '',
                                            planId: p.id,
                                            area: eq?.area || '',
                                            section: eq?.section || ''
                                        }));
                                        
                                        if (updatePlan && p.status !== PlanStatus.COMPLETED) {
                                            updatePlan({ ...p, status: PlanStatus.IN_PROGRESS });
                                        }
                                    }}
                                    className={`w-12 h-12 rounded-2xl flex items-center justify-center transition-all shadow-xl ${isOverdue ? 'bg-rose-600 text-white hover:bg-slate-950 shadow-rose-600/20' : 'bg-white border border-slate-200 text-slate-400 group-hover:bg-emerald-600 group-hover:border-emerald-600 group-hover:text-white'}`}
                                >
                                    <PenLine className="w-6 h-6" />
                                </button>
                            </div>
                        );
                    }) : (
                        <div className="col-span-full py-10 flex flex-col items-center justify-center bg-slate-50 text-slate-400 rounded-3xl border border-dashed border-slate-200">
                           <ShieldCheck className="w-12 h-12 text-emerald-500/30 mb-4" />
                           <p className="text-xs font-black uppercase tracking-[0.2em]">Semua tugas prioritas selesai</p>
                        </div>
                    )}
                </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-8 py-4">
            <button onClick={() => { setMethod('routine'); setForm({...form, inspectionType: InspectionType.ROUTINE}); }} className="group p-16 bg-white border border-slate-200 rounded-[4rem] text-center space-y-8 hover:border-emerald-500 hover:shadow-2xl hover:shadow-emerald-500/10 transition-all">
                <div className="w-28 h-28 bg-emerald-100 text-emerald-600 rounded-[3rem] flex items-center justify-center mx-auto group-hover:rotate-12 transition-transform shadow-inner"><PenLine className="w-14 h-14" /></div>
                <div>
                    <h3 className="text-3xl font-black text-slate-900 uppercase tracking-tighter">Asset Routine</h3>
                    <p className="text-slate-500 mt-2 font-medium">Technical compliance audit for fire & safety units.</p>
                </div>
                <div className="pt-8 font-black text-emerald-600 flex items-center justify-center gap-2 text-[10px] uppercase tracking-[0.2em]">Open Technical Form <ChevronRight className="w-5 h-5" /></div>
            </button>
            <button onClick={() => { setMethod('genba'); setForm({...form, inspectionType: InspectionType.GENBA_WALK}); }} className="group p-16 bg-slate-950 border border-slate-900 rounded-[4rem] text-center space-y-8 hover:shadow-2xl hover:shadow-emerald-500/10 transition-all">
                <div className="w-28 h-28 bg-white/10 text-emerald-400 rounded-[3rem] flex items-center justify-center mx-auto group-hover:scale-110 transition-transform"><Search className="w-14 h-14" /></div>
                <div>
                    <h3 className="text-3xl font-black text-white uppercase tracking-tighter">Genba & Patrol</h3>
                    <p className="text-slate-400 mt-2 font-medium">Comprehensive behavior & condition audit across 14 categories.</p>
                </div>
                <div className="pt-8 font-black text-emerald-400 flex items-center justify-center gap-2 text-[10px] uppercase tracking-[0.2em]">Launch Audit Engine <ChevronRight className="w-5 h-5" /></div>
            </button>
          </div>
        </div>
      ) : (
        <div className="bg-white p-10 lg:p-14 rounded-[4rem] border border-slate-200 shadow-sm animate-in slide-in-from-bottom-12 duration-500">
            <div className="flex flex-col lg:flex-row items-start justify-between mb-16 gap-8">
                <button onClick={() => setMethod(null)} className="text-[10px] font-black uppercase text-slate-400 hover:text-rose-600 flex items-center gap-2 tracking-widest transition-all">
                   <X className="w-7 h-7" /> Cancel Report
                </button>
                
                <div className="w-full lg:max-w-5xl">
                    <p className="text-[9px] font-black text-slate-400 uppercase tracking-widest mb-4 ml-2">Protocol Category Selector:</p>
                    <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-2 bg-slate-50 p-2 rounded-[2.5rem] border border-slate-100 shadow-inner overflow-hidden">
                        {method === 'genba' ? (
                            observationTypes.map(type => (
                                <button 
                                    key={type}
                                    type="button"
                                    onClick={() => setForm({...form, inspectionType: type})}
                                    className={`px-3 py-4 rounded-2xl text-[8px] font-black uppercase tracking-tight transition-all flex flex-col items-center justify-center gap-2 text-center leading-tight ${form.inspectionType === type ? 'bg-slate-900 text-white shadow-lg' : 'text-slate-400 hover:bg-slate-200'}`}
                                >
                                    <div className={form.inspectionType === type ? 'text-emerald-400' : 'text-slate-300'}>{INSPECTION_TYPE_ICONS[type]}</div>
                                    {type}
                                </button>
                            ))
                        ) : (
                            <div className="col-span-full px-10 py-5 bg-emerald-600 text-white rounded-2xl text-[10px] font-black uppercase tracking-widest flex items-center justify-center gap-3">
                                <ShieldCheck className="w-5 h-5" /> {InspectionType.ROUTINE}
                            </div>
                        )}
                    </div>
                </div>
            </div>

            <form onSubmit={handleSubmit} className="max-w-7xl mx-auto space-y-20">
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-24">
                    <div className="space-y-16">
                        <div>
                            <div className="flex justify-between items-center mb-10 pb-4 border-b">
                              <h3 className="text-xs font-black text-slate-900 uppercase tracking-[0.3em] flex items-center gap-4">
                                  <MapPin className="w-6 h-6 text-emerald-500" /> Audit Location
                              </h3>
                              {isRoutine && (
                                <button type="button" onClick={() => setShowScanner(true)} className="flex items-center gap-2 bg-indigo-600 text-white px-4 py-2 rounded-xl text-[10px] font-black uppercase tracking-widest shadow-lg shadow-indigo-600/20 animate-pulse">
                                  <QrCode className="w-4 h-4" /> Scan Asset QR
                                </button>
                              )}
                            </div>
                            <div className="space-y-8">
                                <div className="grid grid-cols-2 gap-6">
                                    <div>
                                        <label className="block text-[10px] font-black uppercase text-slate-400 tracking-widest mb-3">Plant / Building</label>
                                        <select required value={form.area} onChange={e => setForm({...form, area: e.target.value, section: '', equipmentId: ''})} className="w-full px-8 py-5 bg-slate-50 border border-slate-200 rounded-[2rem] font-bold outline-none">
                                            {areaConfigs.map(a => <option key={a.id} value={a.name}>{a.name}</option>)}
                                        </select>
                                    </div>
                                    <div>
                                        <label className="block text-[10px] font-black uppercase text-slate-400 tracking-widest mb-3">Seksi / Dept</label>
                                        <select required value={form.section} onChange={e => setForm({...form, section: e.target.value, equipmentId: ''})} className="w-full px-8 py-5 bg-slate-50 border border-slate-200 rounded-[2rem] font-bold outline-none">
                                            <option value="">-- PILIH SEKSI --</option>
                                            {areaConfigs.find(a => a.name === form.area)?.sections.map(s => <option key={s.name} value={s.name}>{s.name}</option>)}
                                        </select>
                                    </div>
                                </div>

                                {form.section && (
                                    <div className="p-8 bg-slate-950 text-white rounded-[2.5rem] flex items-center justify-between shadow-2xl animate-in zoom-in duration-300">
                                        <div className="flex items-center gap-5">
                                            <div className="w-12 h-12 bg-emerald-500 text-slate-950 rounded-2xl flex items-center justify-center"><Users className="w-6 h-6" /></div>
                                            <div>
                                                <p className="text-[9px] font-black uppercase text-emerald-400 tracking-widest">Section PIC</p>
                                                <p className="text-lg font-black tracking-tighter">{form.pic}</p>
                                            </div>
                                        </div>
                                        <div className="text-[10px] font-black text-emerald-400 uppercase tracking-widest px-3 py-1 bg-white/5 rounded-lg">Auto-Assigned</div>
                                    </div>
                                )}

                                {isRoutine && form.section && (
                                    <div className="animate-in slide-in-from-top-4">
                                        <label className="block text-[10px] font-black uppercase text-slate-400 tracking-widest mb-3">Pilih Aset Peralatan</label>
                                        <select 
                                            required 
                                            value={form.equipmentId} 
                                            onChange={e => {
                                                const eqId = e.target.value;
                                                const relevantPlan = plans.find(p => 
                                                    p.equipmentId === eqId && 
                                                    p.plannedDate === todayStr && // Prioritize today's plan
                                                    p.status !== PlanStatus.COMPLETED
                                                ) || plans.find(p => 
                                                    p.equipmentId === eqId && 
                                                    p.plannedDate < todayStr && // Fallback to oldest overdue plan
                                                    p.status !== PlanStatus.COMPLETED
                                                );

                                                setForm(prev => ({
                                                    ...prev, 
                                                    equipmentId: eqId,
                                                    planId: relevantPlan?.id || ''
                                                }));

                                                if (relevantPlan && updatePlan && relevantPlan.status !== PlanStatus.COMPLETED) {
                                                    updatePlan({ ...relevantPlan, status: PlanStatus.IN_PROGRESS });
                                                }
                                            }}
                                            className="w-full px-8 py-5 bg-emerald-50 border border-emerald-100 rounded-[2rem] font-black text-xs uppercase tracking-widest outline-none focus:ring-4 focus:ring-emerald-500/10"
                                        >
                                            <option value="">-- PILIH UNIT ASET --</option>
                                            {filteredEquipments.map(e => <option key={e.id} value={e.id}>{e.code} - {e.type}</option>)}
                                        </select>
                                    </div>
                                )}
                            </div>
                        </div>

                        {isRoutine && selectedAssetInfo && (
                            <div className="space-y-8 animate-in slide-in-from-bottom-8">
                                <h3 className="text-xs font-black text-slate-900 uppercase tracking-[0.3em] flex items-center gap-4 mb-2">
                                    <ClipboardCheck className="w-6 h-6 text-emerald-500" /> Technical Checklist
                                </h3>
                                <div className="grid grid-cols-1 gap-3">
                                    {selectedAssetInfo.technicalChecklist.map(item => (
                                        <div 
                                            key={item} 
                                            onClick={() => toggleCheckItem(item)}
                                            className={`p-6 rounded-3xl border flex items-center justify-between cursor-pointer transition-all ${checkItems[item] ? 'bg-emerald-50 border-emerald-200' : 'bg-rose-50 border-rose-200'}`}
                                        >
                                            <span className={`text-xs font-black uppercase tracking-tight ${checkItems[item] ? 'text-emerald-900' : 'text-rose-900'}`}>{item}</span>
                                            <div className={`w-10 h-10 rounded-xl flex items-center justify-center ${checkItems[item] ? 'bg-emerald-500 text-white' : 'bg-rose-500 text-white'}`}>
                                                {checkItems[item] ? <Check className="w-6 h-6" /> : <X className="w-6 h-6" />}
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            </div>
                        )}
                    </div>

                    <div className="space-y-16">
                        <div>
                            <h3 className="text-xs font-black text-slate-900 uppercase tracking-[0.3em] flex items-center gap-4 mb-10 pb-4 border-b">
                                <ShieldAlert className="w-6 h-6 text-rose-500" /> Evidence Recording
                            </h3>
                            <div className="space-y-12">
                                <div>
                                    <label className="block text-[10px] font-black uppercase text-slate-400 tracking-widest mb-4">Capture Field Evidence (Photo)</label>
                                    <div onClick={() => photoInputRef.current?.click()} className="aspect-video bg-slate-50 border-2 border-dashed border-slate-200 rounded-[3rem] flex flex-col items-center justify-center cursor-pointer overflow-hidden group hover:border-emerald-500 transition-all shadow-inner relative">
                                        {form.evidencePhoto ? (
                                            <img src={form.evidencePhoto} className="w-full h-full object-cover" />
                                        ) : (
                                            <div className="text-center">
                                                {isCompressing ? (
                                                  <div className="flex flex-col items-center gap-3">
                                                    <div className="w-10 h-10 border-4 border-slate-200 border-t-emerald-500 rounded-full animate-spin"></div>
                                                    <p className="text-[10px] font-black text-slate-400 uppercase tracking-widest">Optimizing Image...</p>
                                                  </div>
                                                ) : (
                                                  <>
                                                    <div className="w-20 h-20 bg-white rounded-full flex items-center justify-center mx-auto shadow-xl text-slate-300 group-hover:text-emerald-500 transition-all"><Camera className="w-10 h-10" /></div>
                                                    <p className="text-[10px] font-black mt-6 text-slate-400 tracking-[0.3em] uppercase">Take Photo</p>
                                                  </>
                                                )}
                                            </div>
                                        )}
                                        <input type="file" ref={photoInputRef} onChange={async (e) => {
                                            const file = e.target.files?.[0];
                                            if (file) {
                                                setIsCompressing(true);
                                                const reader = new FileReader();
                                                reader.onloadend = async () => {
                                                    try {
                                                      const base64 = reader.result as string;
                                                      const compressed = await compressImage(base64);
                                                      setForm({...form, evidencePhoto: compressed});
                                                    } catch (err) {
                                                      console.error("Compression failed", err);
                                                    } finally {
                                                      setIsCompressing(false);
                                                    }
                                                };
                                                reader.readAsDataURL(file);
                                            }
                                        }} accept="image/*" className="hidden" />
                                    </div>
                                </div>

                                <div>
                                    <label className="block text-[10px] font-black uppercase text-slate-400 tracking-widest mb-3">Audit Findings / Observation Notes</label>
                                    <textarea 
                                        required 
                                        placeholder={`Describe the finding for ${form.inspectionType} specifically...`}
                                        value={form.notes} 
                                        onChange={e => setForm({...form, notes: e.target.value})} 
                                        className="w-full p-10 bg-slate-50 border border-slate-200 rounded-[3rem] h-56 text-sm font-bold outline-none focus:ring-4 focus:ring-emerald-500/10 transition-all"
                                    ></textarea>
                                </div>

                                <div className="pt-10">
                                    <button 
                                      type="submit" 
                                      disabled={isCompressing}
                                      className={`w-full py-8 rounded-[2.5rem] font-black text-xs uppercase tracking-[0.4em] shadow-2xl transition-all flex items-center justify-center gap-4 disabled:opacity-50 ${isObservationMode ? 'bg-slate-900 text-white hover:bg-emerald-600' : 'bg-emerald-600 text-white hover:bg-emerald-700'}`}>
                                        <Zap className="w-6 h-6" /> Sync Audit Data
                                    </button>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </form>
        </div>
      )}

      {showScanner && (
        <div className="fixed inset-0 z-[60] flex items-center justify-center p-6 bg-slate-950/90 backdrop-blur-xl animate-in fade-in">
          <div className="bg-white rounded-[3rem] w-full max-w-xl shadow-2xl overflow-hidden relative">
            <div className="p-8 border-b flex justify-between items-center bg-slate-50">
              <div className="flex items-center gap-3">
                <Maximize className="w-6 h-6 text-indigo-600" />
                <h3 className="text-xl font-black text-slate-900 uppercase tracking-tighter">Asset Scanner</h3>
              </div>
              <button onClick={() => setShowScanner(false)} className="p-2 text-slate-400 hover:text-rose-500"><X className="w-8 h-8" /></button>
            </div>
            
            <div className="p-10">
              <div id="reader" className="w-full overflow-hidden rounded-[2rem] border-4 border-indigo-100"></div>
              <div className="mt-8 p-6 bg-indigo-50 border border-indigo-100 rounded-2xl flex items-center gap-4">
                <div className="w-10 h-10 bg-white rounded-xl flex items-center justify-center shadow-sm"><Info className="w-5 h-5 text-indigo-500" /></div>
                <p className="text-[10px] font-bold text-indigo-900 uppercase tracking-tight leading-relaxed">Posisikan QR Code aset tepat di dalam kotak pemindai untuk sinkronisasi otomatis.</p>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default InputActual;